import React, { useEffect, useState, useRef, useCallback } from 'react';
import { geoMercator, geoPath } from 'd3-geo';
import { feature } from 'topojson-client';

const LATAM_IDS = [
  '484','320','084','340','222','558','188','591',
  '192','214','332','630','170','862','218','604',
  '068','076','600','858','032','152','328','740'
];

const CITIES = [
  { name: 'São Paulo', lat: -23.55, lng: -46.63, color: '#F5C518', r: 3.5 },
  { name: 'Ciudad de México', lat: 19.43, lng: -99.13, color: '#F5C518', r: 3.5 },
  { name: 'Bogotá', lat: 4.71, lng: -74.07, color: '#00D4FF', r: 3 },
  { name: 'Buenos Aires', lat: -34.60, lng: -58.38, color: '#F5C518', r: 3.5 },
  { name: 'Santiago', lat: -33.45, lng: -70.67, color: '#00D4FF', r: 3 },
  { name: 'Lima', lat: -12.05, lng: -77.04, color: '#00E5A0', r: 3 },
];

const LatamMapBackground = () => {
  const [paths, setPaths] = useState([]);
  const [cityPoints, setCityPoints] = useState([]);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 1200, height: 900 });

  const buildMap = useCallback((width, height) => {
    return fetch('/data/countries-110m.json')
      .then(res => res.json())
      .then(topo => {
        const countries = feature(topo, topo.objects.countries);
        const latamFeatures = {
          type: 'FeatureCollection',
          features: countries.features.filter(f => LATAM_IDS.includes(f.id))
        };

        const projection = geoMercator().fitSize([width, height], latamFeatures);
        const pathGenerator = geoPath(projection);

        const generatedPaths = latamFeatures.features.map(f => ({
          id: f.id,
          d: pathGenerator(f)
        }));

        const projectedCities = CITIES.map(city => {
          const [x, y] = projection([city.lng, city.lat]);
          return { ...city, x, y };
        });

        setPaths(generatedPaths);
        setCityPoints(projectedCities);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { clientWidth, clientHeight } = containerRef.current;
        const w = clientWidth || 1200;
        const h = clientHeight || 900;
        setDimensions({ width: w, height: h });
        buildMap(w, h);
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [buildMap]);

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 z-0 pointer-events-none overflow-hidden"
      data-testid="latam-map-background"
    >
      <svg
        width={dimensions.width}
        height={dimensions.height}
        viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
        preserveAspectRatio="xMidYMid slice"
        className="w-full h-full"
        style={{ filter: 'drop-shadow(0 0 6px rgba(0,200,255,0.25))' }}
      >
        <defs>
          <filter id="cityPulse">
            <feGaussianBlur stdDeviation="2.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Country paths */}
        <g>
          {paths.map(p => (
            <path
              key={p.id}
              d={p.d}
              fill="rgba(30, 80, 140, 0.18)"
              stroke="rgba(0, 200, 255, 0.35)"
              strokeWidth="0.6"
              strokeLinejoin="round"
            />
          ))}
        </g>

        {/* Dot grid overlay */}
        <g opacity="0.05">
          {Array.from({ length: Math.ceil(dimensions.width / 30) }, (_, i) =>
            Array.from({ length: Math.ceil(dimensions.height / 30) }, (_, j) => (
              <circle
                key={`dot-${i}-${j}`}
                cx={i * 30}
                cy={j * 30}
                r="0.8"
                fill="#00D4FF"
              />
            ))
          )}
        </g>

        {/* City dots with pulse animation */}
        {cityPoints.map((city, i) => (
          <g key={`city-${i}`} filter="url(#cityPulse)">
            <circle cx={city.x} cy={city.y} r={city.r * 2.5} fill={city.color} opacity="0.15">
              <animate attributeName="r" values={`${city.r * 1.5};${city.r * 3};${city.r * 1.5}`} dur={`${2 + i * 0.5}s`} repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.1;0.25;0.1" dur={`${2 + i * 0.5}s`} repeatCount="indefinite" />
            </circle>
            <circle cx={city.x} cy={city.y} r={city.r} fill={city.color} opacity="0.85">
              <animate attributeName="opacity" values="0.6;1;0.6" dur={`${2 + i * 0.5}s`} repeatCount="indefinite" />
            </circle>
          </g>
        ))}

        {/* Connection lines between cities */}
        {cityPoints.length > 1 && cityPoints.slice(0, -1).map((city, i) => (
          <line
            key={`line-${i}`}
            x1={city.x} y1={city.y}
            x2={cityPoints[i + 1].x} y2={cityPoints[i + 1].y}
            stroke="#00D4FF"
            strokeWidth="0.5"
            strokeDasharray="6 4"
            opacity="0.12"
          >
            <animate attributeName="opacity" values="0.05;0.18;0.05" dur={`${4 + i}s`} repeatCount="indefinite" />
          </line>
        ))}
      </svg>
    </div>
  );
};

export default LatamMapBackground;
